import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

export interface StoryVideo {
  ref: RefObject<HTMLVideoElement | null>;
  muted: boolean;
  playing: boolean;
  /** 0..1 of the length. */
  progress: number;
  toggleMute: () => void;
  togglePlay: () => void;
  onTimeUpdate: () => void;
  onPlay: () => void;
  onPause: () => void;
}

/**
 * The state of the full-screen Story video (design asset-share c6): it starts muted and loops, a tap
 * on the sound button turns the sound on, a tap on the video pauses it. React does not set the
 * `muted` property of a video element, so the hook sets it on the element.
 */
export function useStoryVideo(): StoryVideo {
  const ref = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const video = ref.current;
    if (video === null) {
      return;
    }
    video.muted = muted;
  }, [muted]);

  const toggleMute = useCallback(() => setMuted((value) => !value), []);

  const togglePlay = useCallback(() => {
    const video = ref.current;
    if (video === null) {
      return;
    }
    if (video.paused) {
      video.play().catch(console.error);
      return;
    }
    video.pause();
  }, []);

  const onTimeUpdate = useCallback(() => {
    const video = ref.current;
    if (video === null || !Number.isFinite(video.duration) || video.duration <= 0) {
      return;
    }
    setProgress(video.currentTime / video.duration);
  }, []);

  const onPlay = useCallback(() => setPlaying(true), []);
  const onPause = useCallback(() => setPlaying(false), []);

  return { ref, muted, playing, progress, toggleMute, togglePlay, onTimeUpdate, onPlay, onPause };
}
